import { useDispatch, useSelector } from 'react-redux';
import styles from './styles.module.css';
import { AppDispatch, AppState } from '../../../../../redux/store';
import { FC, ReactNode, useState } from 'react';
import { removeEditTask } from '../../../../../redux/carAllocationEditSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import SelectUnallocatedTask from './SelectUnallocatedTask';
import AllocatePairTask from './AllocatePairTask';
import Confirm from './Confirm';
import SelectCar from './SelectCar';
import SelectDriver from './SelectDriver';
import { createPortal } from 'react-dom';
import { addDays, format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { startDateContext } from './startDateContext';

const labels = {
  unallocatedTask: '未配車の案件から選択',
  cars: '車両を選択',
  drivers: 'ドライバーを選択',
  pairedTask: '付随する作業を選択',
  confirm: '内容の確認',
};
type ModalContent = {
  label: string;
  content: ReactNode;
};
const getContents = (
  step: 1 | 2 | 3 | 4,
  carId?: number,
  userId?: number
): ModalContent => {
  switch (step) {
    case 1:
      return {
        label: labels.unallocatedTask,
        content: <SelectUnallocatedTask />,
      };
    case 2:
      if (!carId) {
        return {
          label: labels.cars,
          content: <SelectCar />,
        };
      } else if (!userId) {
        return {
          label: labels.drivers,
          content: <SelectDriver />,
        };
      } else {
        return {
          label: labels.pairedTask,
          content: <AllocatePairTask />,
        };
      }
    case 3:
      return !carId || !userId
        ? {
            label: labels.pairedTask,
            content: <AllocatePairTask />,
          }
        : {
            label: labels.confirm,
            content: <Confirm />,
          };
    case 4:
      return {
        label: labels.confirm,
        content: <Confirm />,
      };
  }
};
const EditAllocationModal: FC = () => {
  const inEditState = useSelector(
    (state: AppState) => state.carAllocationEdit.editState
  );
  const dispatch = useDispatch<AppDispatch>();
  const onClickBackdrop = () => dispatch(removeEditTask());

  if (!inEditState) {
    return null;
  }
  const modalContent = getContents(
    inEditState.step,
    inEditState.targetCarId,
    inEditState.targetUserId
  );

  return createPortal(
    <div className={styles.backdrop} onClick={onClickBackdrop}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <StartDateLayeredContent>
          <div className={styles.scrollContainer}>
            <div className={styles.contentContainer}>
              {modalContent.content}
            </div>
          </div>
        </StartDateLayeredContent>
      </div>
    </div>,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- public/index.html で定義されている
    document.querySelector('#root')!
  );
};

/**
 * モーダル開閉時に日付の選択状況をリセットするためのデータ保持レイヤー
 */
const StartDateLayeredContent: FC<{ children: ReactNode }> = ({ children }) => {
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  return (
    <startDateContext.Provider value={startDate}>
      <ModalHead />
      <ModalStep startDate={startDate} onChange={setStartDate} />
      {children}
    </startDateContext.Provider>
  );
};

const ModalHead: FC = () => {
  const inEditState = useSelector(
    (state: AppState) => state.carAllocationEdit.editState
  );
  const dispatch = useDispatch<AppDispatch>();
  const onClose = () => dispatch(removeEditTask());
  const startDate = inEditState?.primary.startDate
    ? new Date(inEditState.primary.startDate)
    : undefined;
  const editingModeText = inEditState?.isMoving ? '移動' : '追加';
  const modalHeadText = startDate
    ? `${format(startDate, 'yyyy/M/d(E)H:mm', {
        locale: ja,
      })}~に${editingModeText}します`
    : '';
  return (
    <div className={styles.head}>
      <div className={styles.headText}>{modalHeadText}</div>
      <button onClick={onClose} className={styles.headButton}>
        <FontAwesomeIcon icon={faXmark} className={styles.headButtonIcon} />
      </button>
    </div>
  );
};
const ModalStep: FC<WeekControllerProps> = (props) => {
  const inEditState = useSelector(
    (state: AppState) => state.carAllocationEdit.editState
  );
  if (!inEditState) {
    return null;
  }
  const step = inEditState.step;
  const maxStep =
    !inEditState?.targetCarId || !inEditState?.targetUserId ? 4 : 3;
  const modalContent = getContents(
    inEditState.step,
    inEditState.targetCarId,
    inEditState.targetUserId
  );
  const stepItems =
    maxStep === 3 ? (
      <>
        {step >= 1 && <div className={styles['steps-1-3']}></div>}
        {step >= 2 && <div className={styles['steps-2-3']}></div>}
        {step === 3 && <div className={styles['steps-3-3']}></div>}
      </>
    ) : (
      <>
        {step >= 1 && <div className={styles['steps-1-4']}></div>}
        {step >= 2 && <div className={styles['steps-2-4']}></div>}
        {step >= 3 && <div className={styles['steps-3-4']}></div>}
        {step === 4 && <div className={styles['steps-4-4']}></div>}
      </>
    );

  return (
    <div className={styles.stepsContainer}>
      {!inEditState.isMoving && (
        <div className={styles.steps}>
          {stepItems}
          <div className={styles.stepNumbers}>
            <span className={styles.currentStep}>{`${step}`}</span>
            {`/${maxStep}`}
          </div>
        </div>
      )}
      <div className={styles.stepsLabel}>{modalContent.label}</div>
      {step === 1 && <WeekController {...props}></WeekController>}
    </div>
  );
};

type WeekControllerProps = {
  startDate: string;
  onChange: (value: string) => void;
};
const WeekController: FC<WeekControllerProps> = ({
  startDate: startDateStr,
  onChange,
}) => {
  const startDate = new Date(startDateStr);
  const endDate = addDays(startDate, 6);
  const onNext = () => onChange(format(addDays(startDate, 7), 'yyyy-MM-dd'));
  const onBack = () => onChange(format(addDays(startDate, -7), 'yyyy-MM-dd'));

  return (
    <div className={styles.weekControllerContainer}>
      <div className={styles.week}>
        {`期間: ${format(startDate, 'yyyy/M/d(E)', {
          locale: ja,
        })}~${format(endDate, 'yyyy/M/d(E)', { locale: ja })}`}
      </div>
      <button className={styles.arrowButton} onClick={onBack}>
        <FontAwesomeIcon icon={faAngleLeft} className={styles.arrow} />
      </button>
      <button className={styles.arrowButton} onClick={onNext}>
        <FontAwesomeIcon icon={faAngleRight} className={styles.arrow} />
      </button>
    </div>
  );
};

export default EditAllocationModal;
